import React, { useState } from 'react';
import { Loader2, CheckCircle2, Lightbulb, Bug, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent } from '../services/analytics';
import { api } from '../services/api';

const CATEGORIES = [
  { value: 'feature', label: 'Feature verzoek', icon: <Lightbulb size={14} />, desc: 'Ik mis iets of heb een idee' },
  { value: 'bug',     label: 'Bug melden',      icon: <Bug size={14} />,      desc: 'Iets werkt niet goed' },
  { value: 'other',   label: 'Overig',          icon: <MessageSquare size={14} />, desc: 'Iets anders' },
];

export const FeedbackPage: React.FC = () => {
  const [category, setCategory] = useState('feature');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await api.post('/api/feedback', { category, message: message.trim() });
      setSent(true);
      trackEvent('feedback_submitted', { type: category });
      setMessage('');
      toast.success('Feedback ontvangen, dankjewel!');
      setTimeout(() => setSent(false), 8000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Er ging iets mis');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg">
      <main className="max-w-6xl mx-auto w-full px-6 md:px-10 py-10 md:py-14">
        <div className="mb-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/45 mb-2">Support</p>
          <h1 className="font-sans text-3xl md:text-4xl font-bold tracking-[-0.02em] text-ink">Feedback</h1>
          <p className="text-sm text-ink/55 mt-2 max-w-2xl">
            Jouw input helpt ons Stagery te verbeteren. Deel je idee, meld een bug, of stel een vraag.
          </p>
        </div>

        <section className="bg-white rounded-2xl border border-black/8 p-6 md:p-7 max-w-2xl">
          {sent && (
            <div className="mb-5 flex items-center gap-3 bg-accent/8 border border-accent/20 rounded-lg p-3.5">
              <CheckCircle2 size={18} className="text-accent shrink-0" />
              <div>
                <p className="text-sm font-semibold text-ink">Ontvangen, dankjewel!</p>
                <p className="text-xs text-ink/55">We nemen je feedback serieus.</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-ink/55 mb-2">Type</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {CATEGORIES.map(c => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setCategory(c.value)}
                    className={`text-left rounded-lg border p-3 transition-all ${
                      category === c.value
                        ? 'bg-accent/8 border-accent/40 shadow-[0_0_0_3px_rgba(241,110,0,0.08)]'
                        : 'bg-white border-black/10 hover:border-black/25'
                    }`}
                  >
                    <div className={`flex items-center gap-2 mb-1 ${category === c.value ? 'text-accent' : 'text-ink/70'}`}>
                      {c.icon}
                      <span className="text-sm font-semibold">{c.label}</span>
                    </div>
                    <p className="text-[11px] text-ink/55">{c.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-ink/55 mb-2">Bericht</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={
                  category === 'feature'
                    ? "Bijv: ik zou graag een stijl kunnen kiezen voor de staging (Scandinavisch, Industrieel, etc.)"
                    : category === 'bug'
                    ? 'Beschrijf wat er mis ging. Welke stappen heb je genomen?'
                    : 'Jouw bericht...'
                }
                required
                rows={6}
                className="w-full bg-white border border-black/10 rounded-lg px-3.5 py-3 text-sm focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all resize-none"
              />
            </div>

            {error && <p className="text-xs text-red-600">{error}</p>}

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={loading || !message.trim()}
                className="bg-accent hover:bg-accent-hover text-white px-5 py-2.5 rounded-lg font-semibold text-sm shadow-[0_6px_18px_-6px_rgba(241,110,0,0.5)] hover:shadow-[0_10px_26px_-8px_rgba(241,110,0,0.55)] hover:-translate-y-0.5 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:translate-y-0"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : null}
                {loading ? 'Versturen…' : 'Versturen'}
              </button>
              <p className="text-[11px] text-ink/45">Je naam en e-mail worden automatisch meegestuurd.</p>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
};
